import { useAsyncFn } from "react-use";
import { useTranslation } from "react-i18next";
import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import { StringHelper } from "@/helpers";
import {
  UserCategory,
  UserMemberOptionData,
  UserTenantOptionData,
  UserRead,
  UserService,
  UserOptionMemberChargeFrom,
  UserOptionMemberChargeTime,
} from "@/domains/user";
import {
  UserUserFormData,
  UserBaseOptionFormData,
  UserMemberOptionFormData,
  UserTenantOptionFormData,
  UserApiAccessFormData,
  UserWebAccessFormData,
} from "../types";
// components
import { Modal, Steps } from "antd";
import { Step0Form } from "./Step0Form";
import { Step1Form } from "./Step1Form";
import { Step2Form } from "./Step2Form";
import { Step3Form } from "./Step3Form";
import { Step4Form } from "./Step4Form";

interface UpdateModalProps {
  onUpdate: () => void;
}

export interface UpdateModalRef {
  open: (user: UserRead) => void;
}

export const UpdateModal = forwardRef(
  ({ onUpdate }: UpdateModalProps, ref: Ref<UpdateModalRef>) => {
    const { t } = useTranslation();

    const [showing, setShowing] = useState(false);

    const [currentStep, setCurrentStep] = useState(0);

    const [user, setUser] = useState<UserRead>();

    const [userFormData, setUserFormData] = useState<UserUserFormData>();

    const [baseOptionFormData, setBaseOptionFormData] =
      useState<UserBaseOptionFormData>();

    const [tenantOptionFormData, setTenantOptionFormData] =
      useState<UserTenantOptionFormData>();

    const [memberOptionFormData, setMemberOptionFormData] =
      useState<UserMemberOptionFormData>();

    const [apiAccessOptionFormData, setApiAccessOptionFormData] =
      useState<UserApiAccessFormData>();

    const [webAccessOptionFormData, setWebAccessOptionFormData] =
      useState<UserWebAccessFormData>();

    const [state, updateUser] = useAsyncFn(
      async (
        user: UserRead,
        formData: {
          user: UserUserFormData;
          base: UserBaseOptionFormData;
          tenant: UserTenantOptionFormData;
          member: UserMemberOptionFormData;
          apiAccess: UserApiAccessFormData;
          webAccess: UserWebAccessFormData;
        }
      ) => {
        const payload = {
          user: {
            category: UserCategory.TenantAccount,
            password: formData.user.password1,
            name: formData.user.name,
            option: {
              ...formData.base,
              ...(formData.tenant as unknown as UserTenantOptionData),
              ...(formData.member as unknown as UserMemberOptionData),
              ...(formData.apiAccess as unknown as UserApiAccessFormData),
              ...(formData.webAccess as unknown as UserWebAccessFormData),
            },
          },
        };

        if (
          payload.user.option.member_deposit_charge_from ==
          UserOptionMemberChargeFrom.Member
        ) {
          payload.user.option.member_deposit_charge_time =
            UserOptionMemberChargeTime.Verified;
        }
        if (
          payload.user.option.member_withdraw_charge_from ==
          UserOptionMemberChargeFrom.Member
        ) {
          payload.user.option.member_withdraw_charge_time =
            UserOptionMemberChargeTime.Verified;
        }

        await UserService.update(user.id, payload).run();
      }
    );

    useImperativeHandle(ref, () => {
      setMemberOptionFormData;
      const open = (_user: UserRead) => {
        setUser(_user);

        setCurrentStep(0);

        setUserFormData({
          username: _user.username,
          password1: "",
          password2: "",
          name: _user.name,
          telegram_group_id: "",
        });

        setBaseOptionFormData({
          address: _user.option.address,
          callback: _user.option.callback,
          agency_rate: _user.option.agency_rate,
        });

        setTenantOptionFormData({
          tenant_deposit_expired:
            _user.option.tenant_deposit_expired.toString(),
          tenant_deposit_rate_mode: _user.option.tenant_deposit_rate_mode,
          tenant_deposit_rate_data:
            _user.option.tenant_deposit_rate_data.toString(),
          tenant_deposit_charge_mode: _user.option.tenant_deposit_charge_mode,
          tenant_deposit_charge_rate: _user.option.tenant_deposit_charge_rate,
          tenant_deposit_charge_data:
            _user.option.tenant_deposit_charge_data.toString(),
          tenant_deposit_amount_min:
            _user.option.tenant_deposit_amount_min.toString(),
          tenant_deposit_amount_max:
            _user.option.tenant_deposit_amount_max.toString(),
          tenant_deposit_block_seconds:
            _user.option.tenant_deposit_block_seconds,
          tenant_deposit_block_times: _user.option.tenant_deposit_block_times,
          tenant_deposit_block_span: _user.option.tenant_deposit_block_span,

          tenant_withdraw_expired:
            _user.option.tenant_withdraw_expired.toString(),
          tenant_withdraw_rate_mode: _user.option.tenant_withdraw_rate_mode,
          tenant_withdraw_rate_data:
            _user.option.tenant_withdraw_rate_data.toString(),
          tenant_withdraw_charge_mode: _user.option.tenant_withdraw_charge_mode,
          tenant_withdraw_charge_rate: _user.option.tenant_withdraw_charge_rate,
          tenant_withdraw_charge_data:
            _user.option.tenant_withdraw_charge_data.toString(),
          tenant_withdraw_amount_min:
            _user.option.tenant_withdraw_amount_min.toString(),
          tenant_withdraw_amount_max:
            _user.option.tenant_withdraw_amount_max.toString(),
          tenant_withdraw_block_seconds:
            _user.option.tenant_withdraw_block_seconds,
          tenant_withdraw_block_times: _user.option.tenant_withdraw_block_times,
          tenant_withdraw_block_span: _user.option.tenant_withdraw_block_span,
        });

        setMemberOptionFormData({
          member_deposit_callback: _user.option.member_deposit_callback,
          member_deposit_expired:
            _user.option.member_deposit_expired.toString(),
          member_deposit_currency: _user.option.member_deposit_currency,
          member_deposit_round: _user.option.member_deposit_round,
          member_deposit_rate_mode: _user.option.member_deposit_rate_mode,
          member_deposit_rate_data:
            _user.option.member_deposit_rate_data.toString(),
          member_deposit_charge_time: _user.option.member_deposit_charge_time,
          member_deposit_charge_from: _user.option.member_deposit_charge_from,
          member_deposit_charge_mode: _user.option.member_deposit_charge_mode,
          member_deposit_charge_rate: _user.option.member_deposit_charge_rate,
          member_deposit_charge_data:
            _user.option.member_deposit_charge_data.toString(),
          member_deposit_amount_min:
            _user.option.member_deposit_amount_min.toString(),
          member_deposit_amount_max:
            _user.option.member_deposit_amount_max.toString(),
          member_deposit_block_seconds:
            _user.option.member_deposit_block_seconds,
          member_deposit_block_times: _user.option.member_deposit_block_times,
          member_deposit_block_span: _user.option.member_deposit_block_span,

          member_withdraw_callback: _user.option.member_withdraw_callback,
          member_withdraw_expired:
            _user.option.member_withdraw_expired.toString(),
          member_withdraw_currency: _user.option.member_withdraw_currency,
          member_withdraw_round: _user.option.member_withdraw_round,
          member_withdraw_rate_mode: _user.option.member_withdraw_rate_mode,
          member_withdraw_rate_data:
            _user.option.member_withdraw_rate_data.toString(),
          member_withdraw_charge_time: _user.option.member_withdraw_charge_time,
          member_withdraw_charge_from: _user.option.member_withdraw_charge_from,
          member_withdraw_charge_mode: _user.option.member_withdraw_charge_mode,
          member_withdraw_charge_rate: _user.option.member_withdraw_charge_rate,
          member_withdraw_charge_data:
            _user.option.member_withdraw_charge_data.toString(),
          member_withdraw_amount_min:
            _user.option.member_withdraw_amount_min.toString(),
          member_withdraw_amount_max:
            _user.option.member_withdraw_amount_max.toString(),
          member_withdraw_block_seconds:
            _user.option.member_withdraw_block_seconds,
          member_withdraw_block_times: _user.option.member_withdraw_block_times,
          member_withdraw_block_span: _user.option.member_withdraw_block_span,
        });

        setApiAccessOptionFormData({
          ...StringHelper.replaceCommaValues(
            _user.option,
            [
              "api_white_ips",
              "api_member_withdraw_white_ips",
              "api_member_deposit_white_ips",
              "api_black_ips",
              "api_member_withdraw_black_ips",
              "api_member_deposit_black_ips",
            ],
            { exclusive: true }
          ),
          api_access_mode: _user.option.api_access_mode,
        });

        setWebAccessOptionFormData({
          ...StringHelper.replaceCommaValues(
            _user.option,
            ["web_white_ips", "web_black_ips"],
            { exclusive: true }
          ),
          web_access_mode: _user.option.web_access_mode,
        });

        setShowing(true);
      };

      return { open };
    });

    const handleCancel = () => {
      setShowing(false);
    };

    const handleSubmit = async (_formData: {
      user: UserUserFormData;
      base: UserBaseOptionFormData;
      tenant: UserTenantOptionFormData;
      member: UserMemberOptionFormData;
      apiAccess: UserApiAccessFormData;
      webAccess: UserWebAccessFormData;
    }) => {
      if (!user) return;

      const formData = {
        ..._formData,
        apiAccess: StringHelper.replaceBreakLineValues(_formData.apiAccess, [
          "api_white_ips",
          "api_member_withdraw_white_ips",
          "api_member_deposit_white_ips",
          "api_black_ips",
          "api_member_withdraw_black_ips",
          "api_member_deposit_black_ips",
        ]),
        webAccess: StringHelper.replaceBreakLineValues(_formData.webAccess, [
          "web_white_ips",
          "web_black_ips",
        ]),
      };

      const res = await updateUser(user, formData);
      if (res as unknown) {
        // error
        console.log(res);
        return;
      }

      setShowing(false);

      onUpdate();
    };

    return (
      <Modal
        centered
        maskClosable={false}
        width={760}
        title={t("domains.Tenant.updateTenant")}
        open={showing}
        footer={[]}
        onCancel={handleCancel}
      >
        <Steps
          size="small"
          className="mt:16 px:12"
          current={currentStep}
          items={[
            { title: t("domains.Tenant.basicSetting") },
            { title: t("domains.Tenant.tenantSetting") },
            { title: t("domains.Tenant.memberSetting") },
            { title: t("domains.Tenant.integrationIPManagement") },
            { title: t("domains.Tenant.loginIPManagement") },
          ]}
          onChange={(value) => {
            setCurrentStep(value);
          }}
        ></Steps>

        {userFormData && baseOptionFormData && (
          <Step0Form
            data={{ user: userFormData, base: baseOptionFormData }}
            visible={currentStep === 0}
            onNext={(data) => {
              setUserFormData((originData) => ({
                ...originData,
                ...data.user,
              }));
              setBaseOptionFormData((originData) => ({
                ...originData,
                ...data.base,
              }));
              setCurrentStep(1);
            }}
            onCancel={handleCancel}
          />
        )}

        {tenantOptionFormData && (
          <Step1Form
            data={tenantOptionFormData}
            visible={currentStep === 1}
            onPrev={() => setCurrentStep(0)}
            onNext={(data) => {
              setTenantOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));
              setCurrentStep(2);
            }}
            onCancel={handleCancel}
          />
        )}

        {memberOptionFormData && (
          <Step2Form
            data={memberOptionFormData}
            visible={currentStep === 2}
            onPrev={() => setCurrentStep(1)}
            onNext={(data) => {
              setMemberOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));
              setCurrentStep(3);
            }}
            onCancel={handleCancel}
          />
        )}

        {apiAccessOptionFormData && (
          <Step3Form
            data={apiAccessOptionFormData}
            visible={currentStep === 3}
            onPrev={() => setCurrentStep(2)}
            onNext={(data) => {
              setApiAccessOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));
              setCurrentStep(4);
            }}
            onCancel={handleCancel}
          />
        )}

        {webAccessOptionFormData && (
          <Step4Form
            data={webAccessOptionFormData}
            visible={currentStep === 4}
            loading={state.loading}
            onPrev={() => setCurrentStep(3)}
            onNext={async (data) => {
              setWebAccessOptionFormData((originData) => ({
                ...originData,
                ...data,
              }));

              await handleSubmit({
                user: userFormData!,
                base: baseOptionFormData!,
                tenant: tenantOptionFormData!,
                member: memberOptionFormData!,
                apiAccess: apiAccessOptionFormData!,
                webAccess: {
                  ...webAccessOptionFormData,
                  ...data,
                },
              });
            }}
            onCancel={handleCancel}
          />
        )}
      </Modal>
    );
  }
);
